/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import { cloneDeep } from 'lodash';
import Types from '../constants/actionTypes';

const initialState = {
  criteria: {
    accepted: {},
    done: { team: [] },
    ready: { team: [] },
    shippable: { team: [] },
    success: {}
  },
  isFetching: false,
  isUploading: false
};

const groupCriteria = (currentCriteria, newCriteria, crudAction = null) => {
  const criteria = currentCriteria;
  // reference to one of the objects in state

  if (crudAction === 'delete') {
    // eslint-disable-next-line no-restricted-syntax
    for (const wpid in criteria) {
      if (wpid === 'team') continue;
      const associatedCriteria = newCriteria.filter(
        (criterion) => criterion.work_package_id === parseInt(wpid, 10)
      );
      criteria[wpid] = associatedCriteria;
    }
    return criteria;
  }

  const newCriteriaIds = newCriteria.map((nc) => nc.id);

  newCriteria.forEach((newCriterion) => {
    // loop over the criteria that's fetched
    const workPackageCriteria = criteria[newCriterion.work_package_id] || [];
    // the criteria of the work package that previously existed

    const existingCriteria = workPackageCriteria.filter((existingCriterion) =>
      newCriteriaIds.includes(existingCriterion.id)
    );

    if (existingCriteria.length > 0)
      if (existingCriteria.find((c) => c.id === newCriterion.id)) {
        // if existing criteria includes the new criterion
        // update existing criteria
        criteria[newCriterion.work_package_id] = criteria[
          newCriterion.work_package_id
        ].map((c) => {
          if (c.id === newCriterion.id) return newCriterion;

          return c;
        });
      } else {
        // if existing criteria does not include the new criterion
        // we add that new criterion to the work package's criteria
        criteria[newCriterion.work_package_id] = criteria[
          newCriterion.work_package_id
        ].concat(newCriterion);
      }
    else criteria[newCriterion.work_package_id] = [newCriterion];
  });

  return criteria;
};

const groupTeamCriteria = (currentCriteria, newCriteria, dispatchingFn) => {
  let criteria = currentCriteria || [];

  const teamCriteria = newCriteria.filter((criterion) => criterion.team_id);

  teamCriteria.forEach((newCriterion) => {
    const existingCriteria = criteria.find((c) => c.id === newCriterion.id);

    if (existingCriteria)
      criteria = criteria.map((c) => {
        if (c.id === newCriterion.id) return newCriterion;

        return c;
      });
    else criteria = criteria.concat(newCriterion);

    // return newCriterion;
  });

  return criteria;
};

const groupProductCriteria = (currentCriteria, newCriteria) => {
  const criteria = currentCriteria;

  newCriteria.map((newCriterion) => {
    const productCriteria = criteria[newCriterion.product_id] || [];

    const existingCriteria = productCriteria.filter((existingCriterion) =>
      newCriteria.map((nc) => nc.id).includes(existingCriterion.id)
    );
    if (existingCriteria.length > 0)
      if (existingCriteria.find((c) => c.id === newCriterion.id)) {
        criteria[newCriterion.product_id] = criteria[
          newCriterion.product_id
        ].map((c) => {
          if (c.id === newCriterion.id) return newCriterion;

          return c;
        });
      } else {
        criteria[newCriterion.product_id] = criteria[
          newCriterion.product_id
        ].concat(newCriterion);
      }
    else criteria[newCriterion.product_id] = [newCriterion];

    return newCriterion;
  });

  return criteria;
};

const criteriaReducer = (state = initialState, action) => {
  const criteria = cloneDeep(state.criteria);
  const { ready, done, shippable } = criteria;
  const workPackage = action.parent
    ? action.parent
    : action.criteria &&
      ((action.criteria[0] && action.criteria[0].work_package_id) ||
        action.criteria.work_package_id);
  const product = action.criteria?.product_id;

  switch (action.type) {
    case Types.REQUEST_CRITERIA:
      return { ...state, isFetching: true, isUploading: false };
    case Types.RECEIVE_ACCEPTED_CRITERIA:
      if (workPackage)
        criteria.accepted = groupCriteria(criteria.accepted, action.criteria);
      return { ...state, isFetching: false, criteria };
    case Types.RECEIVE_DONE_CRITERIA:
      if (workPackage) {
        criteria.done = groupCriteria(
          criteria.done,
          action.criteria,
          action.crudAction
        );
      }
      criteria.done.team = groupTeamCriteria(
        criteria.done.team,
        action.criteria
      );

      return { ...state, isFetching: false, criteria };
    case Types.RECEIVE_READY_CRITERIA:
      if (workPackage) {
        criteria.ready = groupCriteria(
          criteria.ready,
          action.criteria,
          action.crudAction
        );
      }
      criteria.ready.team = groupTeamCriteria(
        criteria.ready.team,
        action.criteria
      );
      return { ...state, isFetching: false, criteria };
    case Types.RECEIVE_SHIPPABLE_CRITERIA:
      if (workPackage) {
        criteria.shippable = groupCriteria(
          criteria.shippable,
          action.criteria,
          action.crudAction
        );
      }
      criteria.shippable.team = groupTeamCriteria(
        criteria.shippable.team,
        action.criteria
      );

      return { ...state, isFetching: false, criteria };
    case Types.RECEIVE_SUCCESS_CRITERIA:
      criteria.success = groupProductCriteria(
        criteria.success,
        action.criteria
      );
      return { ...state, isFetching: false, criteria };
    case Types.RECEIVE_NEW_ACCEPTED_CRITERIA:
      if (workPackage && !criteria.accepted[workPackage]) {
        criteria.accepted[workPackage] = [action.criteria];
      }
      if (
        workPackage &&
        criteria.accepted[workPackage] &&
        criteria.accepted[workPackage].findIndex(
          (item) => item.id === action.criteria.id
        ) === -1
      ) {
        criteria.accepted[workPackage] = criteria.accepted[workPackage].concat(
          action.criteria
        );
      }
      return { ...state, isFetching: false, criteria };
    case Types.RECEIVE_NEW_SUCCESS_CRITERIA:
      if (product) {
        if (criteria.success[product]) {
          criteria.success[product] = criteria.success[product].concat(
            action.criteria
          );
        } else {
          criteria.success[product] = [action.criteria];
        }
      }
      return { ...state, isFetching: false, criteria };

    case Types.RECEIVE_TEAM_CRITERIA:
      ready.team = ready.team
        .filter((criterion) => criterion.team_id !== action.teamId)
        .concat(action.criteria.ready);
      done.team = done.team
        .filter((criterion) => criterion.team_id !== action.teamId)
        .concat(action.criteria.done);
      shippable.team = shippable.team
        .filter((criterion) => criterion.team_id !== action.teamId)
        .concat(action.criteria.done);
      return {
        ...state,
        criteria
      };

    case Types.DELETE_SUCCESS_CRITERIUM:
      if (product)
        criteria.success[product] = criteria.success[product].filter(
          (criterium) => criterium.id !== action.criteria.id
        );
      return { ...state, isFetching: false, criteria: { ...criteria } };
    case Types.RECEIVE_NEW_DONE_CRITERIA:
      criteria.done.team = criteria.done.team.concat(action.criteria);
      return { ...state, isFetching: false, criteria };
    case Types.RECEIVE_NEW_READY_CRITERIA:
      criteria.ready.team = criteria.ready.team.concat(action.criteria);
      return { ...state, isFetching: false, criteria };
    case Types.RECEIVE_NEW_SHIPPABLE_CRITERIA:
      criteria.shippable.team = criteria.shippable.team.concat(action.criteria);
      return { ...state, isFetching: false, criteria };
    case Types.DELETE_CRITERIA:
      return { ...state, isFetching: false, criteria };
    case Types.DELETE_ACCEPTED_CRITERIUM:
      if (workPackage)
        criteria.accepted[workPackage] = criteria.accepted[workPackage].filter(
          (criterium) => criterium.id !== action.criteria.id
        );
      return { ...state, isFetching: false, criteria: { ...criteria } };
    case Types.DELETE_DONE_CRITERIUM:
      criteria.done.team = criteria.done.team.filter(
        (criterium) => criterium.id !== action.criteria.id
      );
      return { ...state, isFetching: false, criteria };
    case Types.DELETE_READY_CRITERIUM:
      criteria.ready.team = criteria.ready.team.filter(
        (criterium) => criterium.id !== action.criteria.id
      );
      return { ...state, isFetching: false, criteria };
    case Types.DELETE_SHIPPABLE_CRITERIUM:
      criteria.shippable.team = criteria.shippable.team.filter(
        (criterium) => criterium.id !== action.criteria.id
      );
      return { ...state, isFetching: false, criteria };
    case Types.CLEAR_RESULT_CRITERIA:
      delete criteria.ready[action.id];
      delete criteria.shippable[action.id];
      delete criteria.done[action.id];

      return {
        ...state,
        criteria
      };
    case Types.SEND_CRITERIA:
      return { ...state, isUploading: true };
    case Types.CLEAR_ACTIVE_SPINNER:
      return { ...state, isFetching: false, isUploading: false };
    default:
      return state;
  }
};

export default criteriaReducer;
